import {MinusCircle, RotateCcw, X} from 'lucide-react'
import React, {FC, ReactNode, useEffect, useId, useState} from 'react'
import {createPortal} from 'react-dom'
import {useIntl} from 'react-intl'

import {
  type FrequencyPer,
  formatFrequency,
  parseDuration,
  parseFrequency,
} from '../../utils/teacherAllocationModel'

const MAX_WEEKLY = 40
const MAX_CONSECUTIVE = 8

type Props = {
  open: boolean
  subject: string
  classLabel: string
  branch: string
  /** The subject is marked as not taught in this section: only Restore is offered. */
  notTaught: boolean
  frequency: string
  duration: string
  /** Disabled while Versi's proposal is previewed on the grid. */
  readOnly?: boolean
  /** Server flow: the ranges the server accepts (weekly 1–50, daily 1–10, consecutive 1–10). */
  maxWeekly?: number
  maxDaily?: number
  maxConsecutive?: number
  /** Server flow: Save changes / Restore is in flight. */
  saving?: boolean
  /** Server flow: the stored frequency can't be read ("–"): the user sets it, Save is offered. */
  frequencyUnset?: boolean
  /** Server flow: days the level has school timings for — a daily value's weekly equivalent. */
  schoolDays?: number
  onSave: (values: {frequency: string; duration: string}) => void
  onClose: () => void
  onMarkNotTaught: () => void
  onRestore: () => void
}

const Stepper: FC<{
  id: string
  value: number
  min: number
  max: number
  unit: string
  decLabel: string
  incLabel: string
  disabled?: boolean
  onChange: (value: number) => void
}> = ({id, value, min, max, unit, decLabel, incLabel, disabled, onChange}) => (
  <div className='ta-stepper'>
    <div className='ta-stepper__control'>
      <button
        type='button'
        aria-label={decLabel}
        disabled={disabled || value <= min}
        onClick={() => onChange(Math.max(min, value - 1))}
      >
        −
      </button>
      <input
        id={id}
        inputMode='numeric'
        value={value}
        disabled={disabled}
        onChange={(e) => {
          const n = Number(e.target.value.replace(/\D/g, ''))
          if (Number.isFinite(n)) onChange(Math.min(max, Math.max(min, n || min)))
        }}
      />
      <button
        type='button'
        aria-label={incLabel}
        disabled={disabled || value >= max}
        onClick={() => onChange(Math.min(max, value + 1))}
      >
        +
      </button>
    </div>
    <span className='ta-stepper__unit'>{unit}</span>
  </div>
)

/**
 * Right-side slide-over for one grid cell (handover, 3B). Both fields start with the current
 * values; the summary follows the edits; Save changes applies them and Cancel / close discards
 * them. These are teaching requirements — they never change the assignment count. "Mark as not
 * taught" removes this section's requirement (with confirmation, raised by the caller).
 */
const CellSettingsPanel: FC<Props> = ({
  open,
  subject,
  classLabel,
  branch,
  notTaught,
  frequency,
  duration,
  readOnly,
  maxWeekly = MAX_WEEKLY,
  maxDaily = MAX_WEEKLY,
  maxConsecutive = MAX_CONSECUTIVE,
  saving,
  frequencyUnset,
  schoolDays,
  onSave,
  onClose,
  onMarkNotTaught,
  onRestore,
}) => {
  const intl = useIntl()
  const t = (id: string, values?: Record<string, ReactNode>) =>
    intl.formatMessage({id: `ONBOARDING_WIZARD.TEACHER_ALLOCATION.SETTINGS.${id}`}, values as any)
  const weeklyId = useId()
  const consecutiveId = useId()
  const titleId = useId()

  const initial = parseFrequency(frequency)
  const [weekly, setWeekly] = useState(initial.count)
  const [per, setPer] = useState<FrequencyPer>(initial.per)
  const [consecutive, setConsecutive] = useState(parseDuration(duration))

  // Every opening starts from the cell's saved values.
  useEffect(() => {
    if (!open) return
    const parsed = parseFrequency(frequency)
    setWeekly(parsed.count)
    setPer(parsed.per)
    setConsecutive(parseDuration(duration))
  }, [open, frequency, duration, subject, classLabel])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  const dirty =
    Boolean(frequencyUnset) || weekly !== initial.count || consecutive !== parseDuration(duration)
  const unit = per === 'day' ? t('PER_DAY') : t('PER_WEEK')

  return createPortal(
    <div className='ta-sheet-root'>
      <div className='ta-sheet-backdrop' onClick={onClose} aria-hidden />
      <aside className='ta-sheet' role='dialog' aria-modal='true' aria-labelledby={titleId}>
        <div className='ta-sheet__head'>
          <h2 id={titleId}>{t('TITLE')}</h2>
          <button type='button' className='ta-icon-btn' aria-label={t('CLOSE')} onClick={onClose}>
            <X size={22} aria-hidden />
          </button>
        </div>

        <div className='ta-sheet__identity'>
          <div className='ta-sheet__subject'>
            {subject} · {classLabel}
          </div>
          <div className='ta-sheet__branch'>{branch}</div>
        </div>

        {notTaught ? (
          <div className='ta-sheet__section'>
            <p className='ta-sheet__help'>
              {t('NOT_TAUGHT_STATE', {subject, section: classLabel})}
            </p>
            <button
              type='button'
              className='ta-btn ta-btn--primary'
              onClick={onRestore}
              disabled={readOnly || saving}
            >
              {saving ? (
                <span className='spinner-border spinner-border-sm' aria-hidden />
              ) : (
                <RotateCcw size={16} aria-hidden />
              )}
              {t('RESTORE')}
            </button>
            <p className='ta-sheet__footnote'>
              {t('RESTORE_HELP', {subject, section: classLabel})}
            </p>
          </div>
        ) : (
          <>
            <div className='ta-sheet__section'>
              <label className='ta-sheet__label' htmlFor={weeklyId}>
                {t('WEEKLY_LABEL')}
              </label>
              {frequencyUnset && <p className='ta-sheet__help'>{t('WEEKLY_UNSET')}</p>}
              <Stepper
                id={weeklyId}
                value={weekly}
                min={1}
                max={per === 'day' ? maxDaily : maxWeekly}
                unit={unit}
                decLabel={t('DECREASE_WEEKLY')}
                incLabel={t('INCREASE_WEEKLY')}
                disabled={readOnly}
                onChange={setWeekly}
              />
              <p className='ta-sheet__help'>
                {per === 'day'
                  ? t('WEEKLY_HELP_DAY', {subject, section: classLabel})
                  : t('WEEKLY_HELP', {subject, section: classLabel})}
              </p>

              <label className='ta-sheet__label' htmlFor={consecutiveId}>
                {t('CONSECUTIVE_LABEL')}
              </label>
              <Stepper
                id={consecutiveId}
                value={consecutive}
                min={1}
                max={maxConsecutive}
                unit={t('PERIOD_UNIT', {count: consecutive})}
                decLabel={t('DECREASE_CONSECUTIVE')}
                incLabel={t('INCREASE_CONSECUTIVE')}
                disabled={readOnly}
                onChange={setConsecutive}
              />
              <p className='ta-sheet__help'>
                {t('CONSECUTIVE_HELP', {subject})}
                <br />
                {t('CONSECUTIVE_EXAMPLE')}
              </p>

              <div className='ta-sheet__summary' aria-live='polite'>
                {per === 'day'
                  ? t('SUMMARY_DAY', {
                      classes: weekly,
                      periods: consecutive,
                      total: weekly * consecutive,
                    })
                  : t('SUMMARY', {
                      classes: weekly,
                      periods: consecutive,
                      total: weekly * consecutive,
                    })}
                {per === 'day' && schoolDays ? (
                  <>
                    <br />
                    {t('DAILY_WEEKLY', {weekly: weekly * schoolDays, days: schoolDays})}
                  </>
                ) : null}
              </div>

              <div className='ta-sheet__actions'>
                <button type='button' className='ta-btn ta-btn--ghost' onClick={onClose}>
                  {t('CANCEL')}
                </button>
                <button
                  type='button'
                  className='ta-btn ta-btn--primary'
                  disabled={readOnly || !dirty || saving}
                  onClick={() =>
                    onSave({frequency: formatFrequency(weekly, per), duration: String(consecutive)})
                  }
                >
                  {saving && <span className='spinner-border spinner-border-sm' aria-hidden />}
                  {t('SAVE')}
                </button>
              </div>
            </div>

            <div className='ta-sheet__section ta-sheet__danger'>
              <h3>{t('NOT_TAUGHT_TITLE')}</h3>
              <p className='ta-sheet__help'>
                {t('NOT_TAUGHT_TEXT', {subject, section: classLabel})}
              </p>
              <button
                type='button'
                className='ta-btn ta-btn--danger-outline ta-btn--block'
                onClick={onMarkNotTaught}
                disabled={readOnly}
              >
                <MinusCircle size={18} aria-hidden />
                {t('MARK_NOT_TAUGHT', {section: classLabel})}
              </button>
              <p className='ta-sheet__footnote'>
                {t('NOT_TAUGHT_SCOPE', {section: classLabel, branch})}
              </p>
            </div>
          </>
        )}
      </aside>
    </div>,
    document.body
  )
}

export default CellSettingsPanel
